import React, { useState, useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { Modal, Form, Input, Button, DatePicker, Flex, Row, Col, Card, Checkbox, Typography } from "antd"
import { UserOutlined } from "@ant-design/icons"
import styles from "./index.module.css"
import LabeledField from "pagesV2/client/Profile/Tabs/ProfileInfo/LabeledField"
import bgMan from "assets/imagesV2/logo_man.svg"
import bgWoman from "assets/imagesV2/logo_woman.svg"
import { useTheme } from "context/AntdThemeContext"
import { themes } from "constants/theme"
import { Delete } from "ui/Icon/DSIcon/Delete"
import IconWithBg from "ui/IconWithBg"
import { useTranslation } from "react-i18next"
import { postWaiverSubmission, getWaiverSubmissionsByTripBooking } from "store/digital-waiver"
import { useDispatch, useSelector } from "react-redux"
import { toaster } from "components/common/Toast"
import { validatePhoneNumber, PhoneInput } from "ui/Input/PhoneInput"
import dayjs from "dayjs"
import { isEmbeddedContext } from "helpers/embedded"
const { Title } = Typography
export default function MinorModal({ uuid, userId, id, tripBookingId, waiver, visible, onCancel, onSignDocumentOpen, dobDefaultPickerValue = false }) {
  const guardianDefaultPickerValue = dobDefaultPickerValue ? dayjs().subtract(18, "year") : undefined
  const { loading: postLoading } = useSelector((store) => store?.digitalWaiver) || {}
  const guestData = useSelector((state) => state.digitalWaiver.waiverSubmissionsByTripBooking.guest_data) || []
  const pendingChildCount = useMemo(
    () =>
      guestData.filter(
        (g) =>
          g.participant_type === "Child" &&
          g.status === "Unsigned" &&
          (!g.pending_submission || g.pending_submission.length === 0)
      ).length,
    [guestData]
  )
  const { theme } = useTheme()
  const logoSrc = useMemo(() => (theme === themes.sportswoman ? bgWoman : bgMan), [theme])
  const [form] = Form.useForm()
  const [minors, setMinors] = useState([{ fullName: "", dob: null }])
  const { first_name, last_name, email, birthday, country_code, phone_number } = useSelector((state) => state.user)
  const [parentData, setParentData] = useState({ phone: { fullNumber: "", countryCode: "" } })
  const [useMyDetails, setUseMyDetails] = useState(false)
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const handleAddMinor = useCallback(() => {
    setMinors((prev) => [...prev, { fullName: "", dob: null }])
  }, [])

  const handleDeleteMinor = useCallback((idx) => {
    setMinors((prev) => prev.filter((_, i) => i !== idx))
  }, [])

  const handleUseMyDetailsChange = useCallback(
    (checked) => {
      setUseMyDetails(checked)
      if (checked) {
        // Auto-fill form with user data
        const userPhone = {
          fullNumber: phone_number || "",
          countryCode: country_code || ""
        }
        form.setFieldsValue({
          guardianFirstName: first_name || "",
          guardianLastName: last_name || "",
          guardianEmail: email || "",
          guardianRelation: "", // You may need to get this from user state
          guardianPhoneNumber: {
            fullNumber: phone_number || "",
            countryCode: country_code || ""
          },
          guardianDob: birthday ? dayjs(birthday) : null
        })
        setParentData({ phone: userPhone })
      } else {
        // Clear the form
        form.setFieldsValue({
          guardianFirstName: "",
          guardianLastName: "",
          guardianEmail: "",
          guardianRelation: "",
          guardianPhoneNumber: {
            fullNumber: "",
            countryCode: ""
          },
          guardianDob: null
        })
        setParentData({ phone: { fullNumber: "", countryCode: "" } })
      }
    },
    [form, first_name, last_name, email, birthday, country_code, phone_number]
  )

  const handleMinorChange = useCallback((idx, field, value) => {
    setMinors((prev) => {
      const newMinors = [...prev]
      newMinors[idx] = { ...newMinors[idx], [field]: value }
      return newMinors
    })
  }, [])

  const handleNext = useCallback(async () => {
    try {
      await form.validateFields()
      const guardianDetails = form.getFieldsValue()
      const formData = new FormData()
      formData.append("waiver_submission[guardian_first_name]", guardianDetails.guardianFirstName || "")
      formData.append("waiver_submission[guardian_last_name]", guardianDetails.guardianLastName || "")
      formData.append("waiver_submission[guardian_email]", guardianDetails.guardianEmail || "")
      formData.append("waiver_submission[guardian_phone_number]", guardianDetails.guardianPhoneNumber?.fullNumber || "")
      const formattedDate = guardianDetails.guardianDob
        ? new Date(guardianDetails.guardianDob).toLocaleDateString("en-GB").replaceAll("/", "-")
        : ""
      formData.append("waiver_submission[guardian_birthdate]", formattedDate)
      formData.append("waiver_submission[relation]", guardianDetails.guardianRelation || "")
      if (minors && minors.length > 0) {
        if (id) {
          formData.append("waiver_submission[shared_waiver_id]", id)
        } else if (userId) {
          formData.append("waiver_submission[user_id]", userId)
        }
        // Only set trip_booking_id once, prefer tripBookingId, then waiver.trip_booking_id
        if (waiver?.trip_booking_id) {
          formData.append("waiver_submission[trip_booking_id]", waiver.trip_booking_id)
        } else if (tripBookingId) {
          formData.append("waiver_submission[trip_booking_id]", tripBookingId)
        }
        formData.append("waiver_submission[submission_for]", "minor")
        minors.forEach((minor, index) => {
          formData.append(`waiver_submission[waiver_participants_attributes][${index}][participant_type]`, "minor")
          formData.append(`waiver_submission[waiver_participants_attributes][${index}][full_name]`, minor.fullName || "")
          // Format date as DD-MM-YYYY
          const formattedMinorDate = minor.dob ? new Date(minor.dob).toLocaleDateString("en-GB").replaceAll("/", "-") : ""
          formData.append(`waiver_submission[waiver_participants_attributes][${index}][birthdate]`, formattedMinorDate)
        })
      }
      // }
      // setIsModalOpen(true)
      dispatch(postWaiverSubmission(formData)).then((res) => {
        toaster.success(res?.message)
        if (uuid) {
          dispatch(getWaiverSubmissionsByTripBooking(uuid))
        }
        // Call parent to open SignDocumentModal and pass data
        if (onSignDocumentOpen) {
          onSignDocumentOpen(res?.data?.id, res?.data?.pending_waiver_submissions || [])
        }
        // Close minor modal
        handleCancel()
      })
    } catch (err) {
      // Validation errors will be shown by AntD
    }
  }, [form, minors, dispatch])

  const handleCancel = useCallback(() => {
    setMinors([{ fullName: "", dob: null }])
    form.resetFields()
    if (onCancel) onCancel()
  }, [form, onCancel])

  return (
    <Modal
        maskClosable={false}
        open={visible}
        onCancel={handleCancel}
        footer={null}
        closable={true}
        centered
        width={900}
        className={styles.modalContainer}
      >
        <Form form={form} layout="vertical">
          <div className="pb-xl">
            <div className="flex items-center gap-2">
              <UserOutlined style={{ fontSize: "20px" }} className="text-color-primary" />
              <Title level={3} className="text-lg font-bold">
                Guardian Details
              </Title>
            </div>
          </div>

          {!isEmbeddedContext() && localStorage.getItem("access_token") && (
            <Card className={`${styles.guardianDetailsCard} mb-xl`}>
              <Flex align="center" justify="space-between">
                <Flex align="center" gap={10}>
                  <UserOutlined />
                  <div className={styles.loggedInAs}>
                    Logged in as{" "}
                    <span className="text-color-primary">
                      {first_name} {last_name}
                    </span>
                  </div>
                </Flex>
                <Checkbox checked={useMyDetails} onChange={(e) => handleUseMyDetailsChange(e.target.checked)}>
                  Use my details
                </Checkbox>
              </Flex>
            </Card>
          )}

          <Row gutter={16} className="mb-lg">
            <Col span={12}>
              <LabeledField
                labelKey={t("embedded.booking_confirmation.first_name")}
                name="guardianFirstName"
                rules={[{ required: true, message: "Enter first name" }]}
              >
                <Input placeholder="Enter first name" />
              </LabeledField>
            </Col>
            <Col span={12}>
              <LabeledField
                labelKey={t("embedded.booking_confirmation.last_name")}
                name="guardianLastName"
                rules={[{ required: true, message: "Enter last name" }]}
              >
                <Input placeholder="Enter last name" />
              </LabeledField>
            </Col>
          </Row>
          <Row gutter={16} className="mb-lg">
            <Col span={12}>
              <LabeledField
                labelKey={t("embedded.booking_confirmation.email_address")}
                name="guardianEmail"
                rules={[{ required: true, message: "Enter email address" }]}
              >
                <Input placeholder="Enter email address" />
              </LabeledField>
            </Col>
            <Col span={12}>
              <LabeledField
                labelKey={t("embedded.booking_confirmation.relation_with_minor")}
                name="guardianRelation"
                rules={[{ required: true, message: "Enter relation with minor(s)" }]}
              >
                <Input placeholder="Enter relation with minor(s)" />
              </LabeledField>
            </Col>
          </Row>

          <Row gutter={16} className="mb-lg">
            <Col span={12}>
              <LabeledField
                labelKey={t("embedded.booking_confirmation.phone_number")}
                name="guardianPhoneNumber"
                rules={[
                  { required: true,
                    validator: (_, value) => {
                      if (!value?.fullNumber?.trim()) {
                        return Promise.reject(t("form_validation.phone_required"))
                      }
                      return validatePhoneNumber(value.fullNumber)
                    }
                  }
                ]}
              >
                <PhoneInput
                  key={useMyDetails ? "filled" : "empty"}
                  initialCountryCode={parentData?.phone?.countryCode}
                  initialValue={parentData?.phone?.fullNumber}
                  onChange={(value) => setParentData({ ...parentData, phone: value })}
                />
              </LabeledField>
            </Col>
            <Col span={12}>
              <LabeledField
                labelKey={t("embedded.booking_confirmation.dob")}
                name="guardianDob"
                rules={[
                  { required: true, message: "Enter your date of birth" },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve()

                      const today = new Date()
                      const birthDate = new Date(value)
                      const age = today.getFullYear() - birthDate.getFullYear()
                      const monthDiff = today.getMonth() - birthDate.getMonth()
                      const dayDiff = today.getDate() - birthDate.getDate()

                      const actualAge = monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age

                      if (actualAge < 18) {
                        return Promise.reject("Date of birth should be greater than 18 years")
                      }

                      return Promise.resolve()
                    }
                  }
                ]}
              >
                <DatePicker className="w-full p-xs" style={{ width: "100%" }} placeholder="MM/DD/YYYY" format="MM/DD/YYYY" defaultPickerValue={guardianDefaultPickerValue} />
              </LabeledField>
            </Col>
          </Row>

          <div className="mb-xl">
            <Checkbox>I confirm I am the legal guardian of the minor(s)</Checkbox>
          </div>

          {minors.map((minor, idx) => {
            const minorHeader = `Minor ${idx + 1}`
            const fullNameName = `fullName${idx}`
            const dobName = `dob${idx}`
            const showDeleteButton = minors.length > 1

            return (
              <React.Fragment key={idx}>
                <div className={`pb-xl ${styles.minorHeader}`}>{minors.length === 1 ? "Minor" : minorHeader}</div>
                <div className={styles.formRow}>
                  <div className={styles.formCol}>
                    <LabeledField
                      labelKey={t("embedded.booking_confirmation.full_name")}
                      name={fullNameName}
                      rules={[{ required: true, message: "Enter minor name" }]}
                    >
                      <Input
                        placeholder="Enter minor name"
                        value={minor.fullName}
                        onChange={(e) => handleMinorChange(idx, "fullName", e.target.value)}
                      />
                    </LabeledField>
                  </div>
                  <div className={styles.formCol}>
                    <LabeledField
                      labelKey={t("embedded.booking_confirmation.dob")}
                      name={dobName}
                      rules={[{ required: true, message: "Enter date of birth" }]}
                    >
                      <DatePicker
                        className="w-full p-xs"
                        style={{ width: "100%" }}
                        placeholder="MM/DD/YYYY"
                        format="MM/DD/YYYY"
                        value={minor.dob}
                        onChange={(date) => handleMinorChange(idx, "dob", date)}
                      />
                    </LabeledField>
                  </div>
                  {showDeleteButton && (
                    <Flex align="end" className="h-full" onClick={() => handleDeleteMinor(idx)}>
                      <IconWithBg className={styles.deleteIcon} height={18} width={18} icon={<Delete fill="#ff0000" />} />
                    </Flex>
                  )}
                </div>
              </React.Fragment>
            )
          })}
          <Button color="primary" variant="outlined" onClick={handleAddMinor} disabled={minors.length >= pendingChildCount}>
            {t("embedded.booking_confirmation.add_minor")}
          </Button>
          <Flex align="center" justify="space-between" gap={8} className="mt-4xl">
            <Flex align="center" gap={12}>
              {t("settings.powered_by")}
              <img src={logoSrc} alt="Digital Sportsman" className={styles.poweredLogo} />
            </Flex>
            <Flex align="center" gap={8}>
              <Button onClick={handleCancel} color="primary" variant="outlined">
                {t("embedded.booking_confirmation.previous")}
              </Button>
              <Button loading={postLoading} disabled={postLoading} color="primary" variant="solid" onClick={handleNext}>
                {t("embedded.booking_confirmation.next")}
              </Button>
            </Flex>
          </Flex>
        </Form>
      </Modal>
  )
}

MinorModal.propTypes = {
  uuid: PropTypes.string,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  id: PropTypes.string,
  tripBookingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  waiver: PropTypes.object,
  visible: PropTypes.bool,
  onCancel: PropTypes.func,
  onSignDocumentOpen: PropTypes.func,
  dobDefaultPickerValue: PropTypes.bool
}
